import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { message, Tag, Badge, Avatar, Button, Input, Modal, Typography, Form, Select, Spin, Tooltip, Card } from 'antd';
import moment from 'moment';
import disputeService from '../services/disputeService';
import adminService from '../services/adminService';
import userService from '../services/userService';
import { useSelector } from 'react-redux';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import PageHeader from '../components/Common/PageHeader';
import { UserOutlined, SendOutlined, UserAddOutlined, CheckCircleOutlined, CrownOutlined, FileTextOutlined, SearchOutlined, LinkOutlined } from '@ant-design/icons';
import env from '../env';
import { disputeMessageTemplates } from '../data/disputeMessageTemplates';
import { resolutionTemplates } from '../data/resolutionTemplates';
import { toTitleCase } from '../utils/format';

const { Text } = Typography;

const DisputeDetail = () => {
    const { disputeNumber } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [messageText, setMessageText] = useState('');
    const [sending, setSending] = useState(false);
    const [messageError, setMessageError] = useState('');
    const [disputeData, setDisputeData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [assignModalVisible, setAssignModalVisible] = useState(false);
    const [resolveModalVisible, setResolveModalVisible] = useState(false);
    const [admins, setAdmins] = useState([]);
    const [selectedAdmin, setSelectedAdmin] = useState('');
    const [resolutionOutcome, setResolutionOutcome] = useState('');
    const [resolutionDetails, setResolutionDetails] = useState('');
    const [templateModalVisible, setTemplateModalVisible] = useState(false);
    const [templateSearchQuery, setTemplateSearchQuery] = useState('');
    const [userDetailModalVisible, setUserDetailModalVisible] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userDetailLoading, setUserDetailLoading] = useState(false);
    const [resolving, setResolving] = useState(false);
    const [resolutionTemplateModalVisible, setResolutionTemplateModalVisible] = useState(false);
    const [resolutionTemplateSearchQuery, setResolutionTemplateSearchQuery] = useState('');
    const [assignForm] = Form.useForm();
    const [resolveForm] = Form.useForm();
    const { currentUser } = useSelector((state) => state.user);
    const isAdmin = currentUser?.roles?.includes('admin') || location.pathname.includes('/admin');

    useEffect(() => {
        loadDispute();
        if (isAdmin) {
            loadAdmins();
        }
    }, [disputeNumber, isAdmin]);

    const loadDispute = async () => {
        try {
            setLoading(true);
            const response = await disputeService.getDispute(disputeNumber.toUpperCase());
            if (response.status === 'success') {
                setDisputeData(response.data);
            } else {
                message.error('Failed to load dispute');
                navigate(isAdmin ? '/admin/disputes' : '/disputes');
            }
        } catch (error) {
            message.error('Failed to load dispute details');
            navigate(isAdmin ? '/admin/disputes' : '/disputes');
        } finally {
            setLoading(false);
        }
    };

    const loadAdmins = async () => {
        try {
            const response = await adminService.getUsers({ role: 'admin' });
            if (response.status === 'success') {
                setAdmins(response.data?.data || []);
            }
        } catch (error) {
            console.error('Failed to load admins:', error);
        }
    };

    const handleSendMessage = async () => {
        const trimmedMessage = messageText.trim();
        
        if (!trimmedMessage) {
            setMessageError('Please enter a message');
            message.warning('Please enter a message');
            return;
        }

        if (trimmedMessage.length < 10) {
            setMessageError('The message field must be at least 10 characters.');
            message.warning('The message field must be at least 10 characters.');
            return;
        }

        setMessageError('');

        try {
            setSending(true);
            const response = await disputeService.addMessage(disputeData.dispute_number, messageText);
            if (response.status === 'success') {
                setMessageText('');
                setMessageError('');
                setDisputeData(response.data);
                message.success('Message sent successfully');
            }
        } catch (error) {
            let errorMessage = '';
            
            // Handle different error response formats
            if (error.response?.data?.errors?.message) {
                const msgError = error.response.data.errors.message;
                errorMessage = Array.isArray(msgError) ? msgError[0] : msgError;
            } else if (error.response?.data?.message) {
                const msgError = error.response.data.message;
                errorMessage = Array.isArray(msgError) ? msgError[0] : msgError;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else {
                errorMessage = 'Failed to send message';
            }
            
            setMessageError(errorMessage);
            message.error(errorMessage);
        } finally {
            setSending(false);
        }
    };

    const handleCancelDispute = () => {
        Modal.confirm({
            title: 'Cancel Dispute',
            content: 'Are you sure you want to cancel this dispute? This action cannot be undone.',
            okText: 'Yes, Cancel Dispute',
            okType: 'danger',
            cancelText: 'No',
            onOk: async () => {
                try {
                    const response = await disputeService.cancelDispute(disputeData.dispute_number);
                    if (response.status === 'success') {
                        message.success('Dispute cancelled successfully');
                        navigate(isAdmin ? '/admin/disputes' : '/disputes');
                    }
                } catch (error) {
                    message.error(error.response?.data?.message || 'Failed to cancel dispute');
                }
            },
        });
    };

    const handleAssignDispute = async (values) => {
        const adminId = values.admin;
        if (!adminId) {
            message.error('Please select an admin');
            return;
        }

        try {
            const response = await disputeService.adminAssignDispute(disputeData.dispute_number, adminId);
            if (response.status === 'success') {
                message.success('Dispute assigned successfully');
                setAssignModalVisible(false);
                setSelectedAdmin('');
                assignForm.resetFields();
                loadDispute();
            }
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to assign dispute');
        }
    };

    const handleResolveDispute = async (values) => {
        try {
            setResolving(true);
            const response = await disputeService.adminResolveDispute(disputeData.dispute_number, {
                resolution_outcome: values.resolutionOutcome || resolutionOutcome,
                resolution: values.resolutionDetails || resolutionDetails
            });
            if (response.status === 'success') {
                message.success('Dispute resolved successfully');
                setResolveModalVisible(false);
                setResolutionOutcome('');
                setResolutionDetails('');
                resolveForm.resetFields();
                loadDispute();
            }
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to resolve dispute');
        } finally {
            setResolving(false);
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'orange',
            under_review: 'blue',
            awaiting_response: 'purple',
            resolved: 'green',
            closed: 'default',
            cancelled: 'red'
        };
        return colors[status] || 'default';
    };

    const getPriorityColor = (priority) => {
        const colors = {
            urgent: 'red',
            high: 'orange',
            medium: 'blue',
            low: 'default'
        };
        return colors[priority] || 'default';
    };

    const getTypeLabel = (type) => {
        const labels = {
            item_not_received: 'Item Not Received',
            item_not_as_described: 'Item Not As Described',
            damaged_item: 'Damaged Item',
            wrong_item: 'Wrong Item',
            payment_issue: 'Payment Issue',
            refund_request: 'Refund Request',
            other: 'Other'
        };
        return labels[type] || type;
    };

    const getResolutionOutcomeLabel = (outcome) => {
        const labels = {
            favor_filer: 'In Favor of Filer',
            favor_respondent: 'In Favor of Respondent',
            partial_refund: 'Partial Refund',
            full_refund: 'Full Refund',
            replacement: 'Replacement',
            no_action: 'No Action Required',
            cancelled: 'Cancelled'
        };
        return labels[outcome] || outcome;
    };

    const canAddMessage = disputeData && 
        !disputeData.status?.includes('closed') && 
        !disputeData.status?.includes('cancelled') &&
        !disputeData.status?.includes('resolved');

    const isFiler = disputeData && currentUser && disputeData.filer?.id === currentUser.id;
    const canCancel = isFiler && disputeData?.status === 'pending';

    // Handle viewing user details
    const handleViewUserDetails = async (userId) => {
        if (!userId || !isAdmin) return;
        
        setUserDetailLoading(true);
        setUserDetailModalVisible(true);
        setSelectedUser(null);
        
        try {
            const response = await userService.getUser(userId);
            setSelectedUser(response.data || response);
        } catch (error) {
            console.error('Error fetching user details:', error);
            message.error('Failed to load user details');
            setUserDetailModalVisible(false);
        } finally {
            setUserDetailLoading(false);
        }
    };

    // Message templates are imported from external file

    const handleTemplateSelect = (template) => {
        setMessageText(template.message);
        setMessageError('');
        setTemplateModalVisible(false);
        setTemplateSearchQuery('');
    };

    // Filter templates based on search query
    const filteredTemplates = disputeMessageTemplates.filter(template => {
        if (!templateSearchQuery.trim()) return true;
        const query = templateSearchQuery.toLowerCase();
        return (
            template.title.toLowerCase().includes(query) ||
            template.message.toLowerCase().includes(query)
        );
    });

    // Handle resolution template selection
    const handleResolutionTemplateSelect = (template) => {
        setResolutionOutcome(template.outcome);
        setResolutionDetails(template.details);
        resolveForm.setFieldsValue({
            resolutionOutcome: template.outcome,
            resolutionDetails: template.details
        });
        setResolutionTemplateModalVisible(false);
        setResolutionTemplateSearchQuery('');
    };

    // Filter resolution templates based on search query
    const filteredResolutionTemplates = resolutionTemplates.filter(template => {
        if (!resolutionTemplateSearchQuery.trim()) return true;
        const query = resolutionTemplateSearchQuery.toLowerCase();
        return (
            template.title.toLowerCase().includes(query) ||
            template.details.toLowerCase().includes(query) ||
            template.outcome.toLowerCase().includes(query)
        );
    });

    // Remove outcome information from message text
    const cleanMessageText = (message) => {
        if (!message) return message;
        // Remove patterns like "Outcome: favor_respondent" or "Outcome: [anything]" with various separators
        let cleaned = message
            .replace(/Outcome:\s*[^\s.,;!?]+[.,;!?]?/gi, '') // Remove "Outcome: [word]" with optional punctuation
            .replace(/\s+/g, ' ') // Replace multiple spaces with single space
            .replace(/\s*\.\s*\./g, '.') // Fix double periods
            .replace(/\s*,\s*,/g, ',') // Fix double commas
            .replace(/^\s*[.,;!?]\s*/g, '') // Remove leading punctuation
            .trim();
        return cleaned;
    };

    return (
        <Spin spinning={loading} tip="Loading dispute details...">
            <DashboardLayout>
                {disputeData && (
                    <>
                        <PageHeader
                            title={
                                <span className="d-flex align-items-center gap-2">
                                    <i className="bi bi-file-text text-primary"></i>
                                    <span>Dispute Details - {disputeData.dispute_number}</span>
                                </span>
                            }
                description={isAdmin ? "View and manage dispute details" : "View and manage your dispute details"}
                buttonText={isAdmin ? "Back to Admin Disputes" : "Back to Disputes"}
                buttonIcon={<i className="bi bi-arrow-left"></i>}
                onButtonClick={() => navigate(isAdmin ? '/admin/disputes' : '/disputes')}
            />

            {/* Resolved Dispute Banner */}
            {(disputeData.status === 'resolved' || disputeData.status === 'closed') && (
                <div 
                    className="mb-4"
                    style={{
                        padding: '16px 20px',
                        background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(82, 196, 26, 0.3)',
                        border: '1px solid rgba(82, 196, 26, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        color: '#ffffff'
                    }}
                >
                    <CheckCircleOutlined style={{ fontSize: '24px', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>
                            Dispute Resolved
                        </div>
                        <div style={{ fontSize: '13px', opacity: 0.95 }}>
                            {disputeData.status === 'resolved' 
                                ? 'This dispute has been successfully resolved.' 
                                : 'This dispute has been closed.'}
                            {disputeData.resolved_at && (
                                <span style={{ marginLeft: '8px', opacity: 0.9 }}>
                                    Resolved on {moment(disputeData.resolved_at).format('MMMM DD, YYYY')}
                                </span>
                            )}
                        </div>
                    </div>
                    {disputeData.resolution_outcome && (
                        <Tag 
                            style={{ 
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                color: '#ffffff',
                                fontSize: '12px',
                                fontWeight: 600,
                                padding: '4px 12px',
                                height: 'auto',
                                borderRadius: '6px'
                            }}
                        >
                            {getResolutionOutcomeLabel(disputeData.resolution_outcome)}
                        </Tag>
                    )}
                </div>
            )}

            <div>
                <div className="row g-4">
                    {/* Main Information Card */}
                    <div className="col-12">
                        <div 
                            className="card shadow-sm border-0"
                            style={{
                                ...(disputeData.status === 'resolved' || disputeData.status === 'closed' ? {
                                    background: 'linear-gradient(135deg, #f6ffed 0%, #ffffff 100%)',
                                    borderLeft: '4px solid #52c41a',
                                    boxShadow: '0 4px 12px rgba(82, 196, 26, 0.15), 0 2px 4px rgba(82, 196, 26, 0.1)'
                                } : {})
                            }}
                        >
                            <div 
                                className="card-header border-bottom"
                                style={{
                                    ...(disputeData.status === 'resolved' || disputeData.status === 'closed' ? {
                                        background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                                        border: 'none'
                                    } : {
                                        background: '#ffffff'
                                    })
                                }}
                            >
                                <h5 
                                    className="card-title mb-0 fw-bold d-flex align-items-center gap-2"
                                    style={{
                                        ...(disputeData.status === 'resolved' || disputeData.status === 'closed' ? {
                                            color: '#ffffff',
                                            textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                                        } : {})
                                    }}
                                >
                                    {(disputeData.status === 'resolved' || disputeData.status === 'closed') && (
                                        <CheckCircleOutlined style={{ fontSize: '20px' }} />
                                    )}
                                    <span>Dispute Information</span>
                                    {(disputeData.status === 'resolved' || disputeData.status === 'closed') && (
                                        <Tag 
                                            color="success" 
                                            style={{ 
                                                marginLeft: 'auto',
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                padding: '4px 12px',
                                                height: 'auto',
                                                lineHeight: '1.5',
                                                borderRadius: '6px',
                                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                                color: '#ffffff'
                                            }}
                                        >
                                            RESOLVED
                                        </Tag>
                                    )}
                                </h5>
                            </div>
                            <div 
                                className="card-body"
                                style={{
                                    ...(disputeData.status === 'resolved' || disputeData.status === 'closed' ? {
                                        backgroundColor: '#f6ffed'
                                    } : {})
                                }}
                            >
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold text-muted small">Dispute Number</label>
                                        <div className="fs-5 fw-bold">{disputeData.dispute_number}</div>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label fw-semibold text-muted small">Status</label>
                                        <div>
                                            <Tag 
                                                color={getStatusColor(disputeData.status)}
                                                style={{
                                                    ...(disputeData.status === 'resolved' || disputeData.status === 'closed' ? {
                                                        fontSize: '13px',
                                                        fontWeight: 600,
                                                        padding: '6px 14px',
                                                        boxShadow: '0 2px 4px rgba(82, 196, 26, 0.3)',
                                                        border: '1px solid rgba(82, 196, 26, 0.3)'
                                                    } : {})
                                                }}
                                            >
                                                {(disputeData.status === 'resolved' || disputeData.status === 'closed') && (
                                                    <CheckCircleOutlined style={{ marginRight: '6px' }} />
                                                )}
                                                {disputeData.status?.replace('_', ' ').toUpperCase()}
                                            </Tag>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label fw-semibold text-muted small">Priority</label>
                                        <div>
                                            <Tag color={getPriorityColor(disputeData.priority)}>
                                                {disputeData.priority?.toUpperCase()}
                                            </Tag>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold text-muted small">Type</label>
                                        <div>{getTypeLabel(disputeData.type)}</div>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold text-muted small">Subject</label>
                                        <div>{disputeData.subject}</div>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold text-muted small">Lot</label>
                                        {disputeData.lot?.slug && disputeData.auction?.slug ? (
                                            <div 
                                                className="text-truncate d-flex align-items-center gap-2" 
                                                style={{ 
                                                    maxWidth: '300px',
                                                    cursor: 'pointer',
                                                    color: '#1890ff',
                                                    textDecoration: 'none',
                                                    transition: 'all 0.2s ease'
                                                }}
                                                title={`Click to view: ${disputeData.lot?.title} (opens in new window)`}
                                                onClick={() => window.open(`/auctions/${disputeData.auction.slug}/lots/${disputeData.lot.slug}`, '_blank')}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.color = '#40a9ff';
                                                    e.currentTarget.style.textDecoration = 'underline';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.color = '#1890ff';
                                                    e.currentTarget.style.textDecoration = 'none';
                                                }}
                                            >
                                                <span className="text-truncate">{disputeData.lot?.title || 'N/A'}</span>
                                                <LinkOutlined style={{ fontSize: '12px', flexShrink: 0 }} />
                                            </div>
                                        ) : (
                                        <div className="text-truncate" style={{ maxWidth: '300px' }} title={disputeData.lot?.title}>
                                            {disputeData.lot?.title || 'N/A'}
                                        </div>
                                        )}
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold text-muted small">Auction</label>
                                        <div>{disputeData.auction?.title || 'N/A'}</div>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold text-muted small">Filer</label>
                                        {isAdmin && disputeData.filer?.id ? (
                                            <div 
                                                style={{ 
                                                    cursor: 'pointer',
                                                    color: '#1890ff',
                                                    transition: 'all 0.2s ease',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    padding: '4px 8px',
                                                    borderRadius: '4px'
                                                }}
                                                title="Click to view user details"
                                                onClick={() => handleViewUserDetails(disputeData.filer.id)}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.color = '#40a9ff';
                                                    e.currentTarget.style.textDecoration = 'underline';
                                                    e.currentTarget.style.backgroundColor = '#f0f7ff';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.color = '#1890ff';
                                                    e.currentTarget.style.textDecoration = 'none';
                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                }}
                                            >
                                                    <span>{toTitleCase(disputeData.filer?.name)}</span>
                                                <UserOutlined style={{ fontSize: '12px' }} />
                                            </div>
                                        ) : (
                                            <div>{toTitleCase(disputeData.filer?.name)}</div>
                                        )}
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold text-muted small">Respondent</label>
                                        {isAdmin && disputeData.respondent?.id ? (
                                            <div 
                                                style={{ 
                                                    cursor: 'pointer',
                                                    color: '#1890ff',
                                                    transition: 'all 0.2s ease',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    padding: '4px 8px',
                                                    borderRadius: '4px'
                                                }}
                                                title="Click to view user details"
                                                onClick={() => handleViewUserDetails(disputeData.respondent.id)}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.color = '#40a9ff';
                                                    e.currentTarget.style.textDecoration = 'underline';
                                                    e.currentTarget.style.backgroundColor = '#f0f7ff';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.color = '#1890ff';
                                                    e.currentTarget.style.textDecoration = 'none';
                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                }}
                                            >
                                                <span>{toTitleCase(disputeData.respondent?.name)}</span>
                                                <UserOutlined style={{ fontSize: '12px' }} />
                                            </div>
                                        ) : (
                                            <div>{toTitleCase(disputeData.respondent?.name)}</div>
                                        )}
                                    </div>
                                    {disputeData.assigned_admin && (
                                        <div className="col-12">
                                            <label className="form-label fw-semibold text-muted small">Assigned Admin</label>
                                            <div>{toTitleCase(disputeData.assigned_admin?.name)}</div>
                                        </div>
                                    )}
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold text-muted small">Created</label>
                                        <div>{moment(disputeData.created_at).format('MMMM DD, YYYY [at] HH:mm')}</div>
                                    </div>
                                    {disputeData.resolved_at && (
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold text-muted small">Resolved</label>
                                            <div>{moment(disputeData.resolved_at).format('MMMM DD, YYYY [at] HH:mm')}</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Description Card */}
                    <div className="col-12">
                        <div 
                            className="card border-0" 
                            style={{ 
                                background: 'linear-gradient(135deg, #fff7e6 0%, #fffbe6 100%)',
                                boxShadow: '0 4px 12px rgba(250, 173, 20, 0.15), 0 2px 4px rgba(250, 173, 20, 0.1)',
                                borderLeft: '4px solid #faad14',
                                borderRadius: '8px',
                                overflow: 'hidden'
                            }}
                        >
                            <div 
                                className="card-header border-bottom" 
                                style={{ 
                                    background: 'linear-gradient(135deg, #faad14 0%, #ffc53d 100%)',
                                    padding: '16px 20px',
                                    border: 'none'
                                }}
                            >
                                <h5 
                                    className="card-title mb-0 fw-bold d-flex align-items-center gap-2"
                                    style={{ 
                                        color: '#ffffff', 
                                        fontSize: '18px',
                                        textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                                    }}
                                >
                                    <i className="bi bi-file-text-fill" style={{ fontSize: '20px' }}></i>
                                    <span>Description</span>
                                </h5>
                            </div>
                            <div 
                                className="card-body" 
                                style={{ 
                                    padding: '20px 24px',
                                    backgroundColor: '#fffbe6'
                                }}
                            >
                                <p 
                                    className="mb-0" 
                                    style={{ 
                                        fontSize: '15px',
                                        lineHeight: '1.8',
                                        color: '#262626',
                                        whiteSpace: 'pre-wrap',
                                        wordWrap: 'break-word'
                                    }}
                                >
                                    {disputeData.description}
                                </p>
                            </div>
                        </div>
                    </div>

                 

                    {/* Messages Card */}
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
                                <h5 className="card-title mb-0 fw-bold d-flex align-items-center gap-2">
                                    <i className="bi bi-chat-dots text-primary"></i>
                                    <span>Messages</span>
                                    {disputeData.messages && disputeData.messages.length > 0 && (
                                        <Badge count={disputeData.messages.length} showZero style={{ backgroundColor: '#1890ff' }} />
                                    )}
                                </h5>
                            </div>
                            <div className="card-body p-0">
                                <div 
                                    style={{ 
                                        padding: '20px',
                                        backgroundColor: '#f8f9fa'
                                    }}
                                    className="messages-container"
                                >
                                    {disputeData.messages && disputeData.messages.length > 0 ? (
                                        <div className="messages-list">
                                            {disputeData.messages.map((msg, index) => {
                                                const isCurrentUser = currentUser && msg.user?.id === currentUser.id;
                                                const isAdminMessage = msg.is_admin;
                                                const isLastMessage = index === disputeData.messages.length - 1;
                                                const userName = msg.user ? 
                                                    `${msg.user.first_name || ''} ${msg.user.last_name || ''}`.trim() || 
                                                    msg.user.email || 
                                                    'User' : 
                                                    'System';
                                                const displayUserName = (userName.includes('@') || userName === 'User' || userName === 'System') 
                                                    ? userName 
                                                    : toTitleCase(userName);
                                                
                                                return (
                                                    <div 
                                                        key={msg.id || index} 
                                                        className={`message-item mb-4 ${isLastMessage ? 'last-message' : ''}`}
                                                        style={{
                                                            display: 'flex',
                                                            flexDirection: 'row',
                                                            gap: '12px',
                                                            animation: isLastMessage ? 'slideInHighlight 0.6s ease-out' : 'fadeIn 0.3s ease-in'
                                                        }}
                                                    >
                                                        <div className="flex-shrink-0">
                                                            <Tooltip title={displayUserName}>
                                                        <Avatar 
                                                            src={msg.user?.profile_image_url || (msg.user?.profile_image ? `${env.BACKEND_STORAGE_URL}/${msg.user.profile_image}` : null)}
                                                            icon={!msg.user?.profile_image_url && !msg.user?.profile_image ? <UserOutlined /> : null}
                                                            style={{ 
                                                                        backgroundColor: isAdminMessage ? '#1890ff' : isCurrentUser ? '#52c41a' : '#87d068',
                                                                        border: isLastMessage 
                                                                            ? '3px solid #fa8c16' 
                                                                            : (isAdminMessage ? '2px solid #1890ff' : '2px solid transparent'),
                                                                        boxShadow: isLastMessage 
                                                                            ? '0 4px 12px rgba(250, 140, 22, 0.4), 0 2px 8px rgba(0,0,0,0.15)' 
                                                                            : '0 2px 8px rgba(0,0,0,0.1)',
                                                                        animation: isLastMessage ? 'avatarPulse 2s ease-in-out infinite' : 'none',
                                                                        transform: isLastMessage ? 'scale(1.05)' : 'scale(1)',
                                                                        transition: 'all 0.3s ease'
                                                            }}
                                                                    size={45}
                                                        >
                                                            {!msg.user?.profile_image_url && !msg.user?.profile_image && msg.user?.first_name ? 
                                                                msg.user.first_name.charAt(0).toUpperCase() : 
                                                                        (!msg.user?.profile_image_url && !msg.user?.profile_image && !isAdminMessage ? 'U' : null)}
                                                        </Avatar>
                                                            </Tooltip>
                                                    </div>
                                                        <div 
                                                            className="flex-grow-1"
                                                            style={{ 
                                                                maxWidth: '70%',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'flex-start'
                                                            }}
                                                        >
                                                            <div 
                                                                className="message-bubble"
                                                                style={{
                                                                    background: isLastMessage 
                                                                        ? 'linear-gradient(135deg, #ffffff 0%, #fff7e6 100%)'
                                                                        : (isAdminMessage ? '#e6f7ff' : '#ffffff'),
                                                                    color: '#000000',
                                                                    padding: '12px 16px',
                                                                    borderRadius: '18px 18px 18px 4px',
                                                                    boxShadow: isLastMessage 
                                                                        ? '0 4px 16px rgba(250, 140, 22, 0.3), 0 2px 8px rgba(0,0,0,0.15)' 
                                                                        : '0 2px 8px rgba(0,0,0,0.1)',
                                                                    border: isLastMessage 
                                                                        ? '2px solid #fa8c16' 
                                                                        : (isAdminMessage ? '1px solid #91d5ff' : 'none'),
                                                                    wordWrap: 'break-word',
                                                                    whiteSpace: 'pre-wrap',
                                                                    position: 'relative',
                                                                    animation: isLastMessage ? 'pulseGlow 2s ease-in-out infinite' : 'none'
                                                                }}
                                                            >
                                                                <div 
                                                                    className="d-flex align-items-center gap-2 mb-2"
                                                                    style={{ 
                                                                        fontSize: '12px',
                                                                        opacity: 0.8,
                                                                        fontWeight: 600
                                                                    }}
                                                                >
                                                                    {isAdminMessage && (
                                                                        <CrownOutlined style={{ color: '#1890ff' }} />
                                                                    )}
                                                                    <span>{displayUserName}</span>
                                                                    {isAdminMessage && (
                                                                        <Tag 
                                                                            color="blue" 
                                                                            size="small"
                                                                            style={{ 
                                                                                margin: 0,
                                                                                fontSize: '10px',
                                                                                padding: '0 6px',
                                                                                height: '18px',
                                                                                lineHeight: '16px'
                                                                            }}
                                                                        >
                                                                            ADMIN
                                                                        </Tag>
                                                                    )}
                                                                    {isLastMessage && (
                                                                        <Tag 
                                                                            color={disputeData.status === 'resolved' || disputeData.status === 'closed' ? 'green' : 'orange'} 
                                                                            size="small"
                                                                            style={{ 
                                                                                margin: 0,
                                                                                fontSize: '10px',
                                                                                padding: '0 6px',
                                                                                height: '18px',
                                                                                lineHeight: '16px',
                                                                                animation: 'pulse 1.5s ease-in-out infinite',
                                                                                fontWeight: 700
                                                                            }}
                                                                        >
                                                                            {disputeData.status === 'resolved' || disputeData.status === 'closed' ? 'CLOSED' : 'NEW'}
                                                                        </Tag>
                                                                        )}
                                                                    </div>
                                                                <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
                                                                    {cleanMessageText(msg.message)}
                                                                </div>
                                                            </div>
                                                            <Tooltip title={moment(msg.timestamp).format('MMMM DD, YYYY [at] HH:mm:ss')}>
                                                                <small 
                                                                    className="text-muted mt-1"
                                                                    style={{ 
                                                                        fontSize: '11px',
                                                                        paddingLeft: '4px'
                                                                    }}
                                                                >
                                                                    {moment(msg.timestamp).fromNow()}
                                                                </small>
                                                            </Tooltip>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-center py-5 text-muted">
                                            <div style={{ fontSize: '64px', marginBottom: '16px', opacity: 0.3 }}>
                                                <i className="bi bi-chat-left-dots"></i>
                                            </div>
                                            <h6 className="mb-2" style={{ color: '#8c8c8c' }}>No messages yet</h6>
                                            <p className="mb-0" style={{ fontSize: '13px' }}>
                                                Start the conversation by sending a message below
                                            </p>
                                        </div>
                                    )}
                                </div>
                                <style>{`
                                    @keyframes fadeIn {
                                        from {
                                            opacity: 0;
                                            transform: translateY(10px);
                                        }
                                        to {
                                            opacity: 1;
                                            transform: translateY(0);
                                        }
                                    }
                                    @keyframes slideInHighlight {
                                        0% {
                                            opacity: 0;
                                            transform: translateX(-20px) translateY(10px);
                                        }
                                        50% {
                                            transform: translateX(5px) translateY(0);
                                        }
                                        100% {
                                            opacity: 1;
                                            transform: translateX(0) translateY(0);
                                        }
                                    }
                                    @keyframes pulseGlow {
                                        0%, 100% {
                                            box-shadow: 0 4px 16px rgba(250, 140, 22, 0.3), 0 2px 8px rgba(0,0,0,0.15);
                                            border-color: #fa8c16;
                                        }
                                        50% {
                                            box-shadow: 0 6px 20px rgba(250, 140, 22, 0.5), 0 4px 12px rgba(0,0,0,0.2);
                                            border-color: #ffa940;
                                        }
                                    }
                                    @keyframes avatarPulse {
                                        0%, 100% {
                                            transform: scale(1.05);
                                            box-shadow: 0 4px 12px rgba(250, 140, 22, 0.4), 0 2px 8px rgba(0,0,0,0.15);
                                        }
                                        50% {
                                            transform: scale(1.08);
                                            box-shadow: 0 6px 16px rgba(250, 140, 22, 0.6), 0 4px 12px rgba(0,0,0,0.2);
                                        }
                                    }
                                    @keyframes pulse {
                                        0%, 100% {
                                            opacity: 1;
                                            transform: scale(1);
                                        }
                                        50% {
                                            opacity: 0.8;
                                            transform: scale(1.05);
                                        }
                                    }
                                `}</style>
                            </div>
                        </div>
                    </div>

                    {/* Add Message Card */}
                    {canAddMessage && (
                        <div className="col-12">
                            <div 
                                className="card border-0" 
                                style={{ 
                                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                                    boxShadow: '0 4px 16px rgba(0,0,0,0.1), 0 2px 8px rgba(0,0,0,0.08)',
                                    borderTop: '3px solid #1890ff',
                                    borderRadius: '12px',
                                    overflow: 'hidden'
                                }}
                            >
                                <div 
                                    className="card-header border-bottom" 
                                    style={{ 
                                        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                        padding: '16px 24px',
                                        border: 'none'
                                    }}
                                >
                                    <h5 
                                        className="card-title mb-0 fw-bold d-flex align-items-center gap-2"
                                        style={{ color: '#ffffff', fontSize: '18px', textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}
                                    >
                                        <i className="bi bi-chat-left-text-fill" style={{ fontSize: '20px' }}></i>
                                        <span>Add a Message</span>
                                    </h5>
                                </div>
                                <div className="card-body" style={{ padding: '24px', backgroundColor: '#ffffff' }}>
                                    <form onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }}>
                                        {/* Message Templates for Admins */}
                                        {isAdmin && (
                                        <div className="mb-3">
                                                <Button
                                                    type="default"
                                                    icon={<FileTextOutlined />}
                                                    onClick={() => setTemplateModalVisible(true)}
                                                    style={{
                                                        borderRadius: '8px',
                                                        height: '36px',
                                                        fontSize: '14px',
                                                        fontWeight: 500,
                                                        borderColor: '#1890ff',
                                                        color: '#1890ff'
                                                    }}
                                                >
                                                    Show Templates
                                                </Button>
                                            </div>
                                        )}
                                        <div className="mb-4">
                                            <label 
                                                className="form-label fw-semibold mb-2 d-flex align-items-center gap-2"
                                                style={{ color: '#262626', fontSize: '14px' }}
                                            >
                                                <i className="bi bi-pencil-square text-primary"></i>
                                                Your Message
                                            </label>
                                            <Input.TextArea
                                                rows={4}
                                                value={messageText}
                                                onChange={(e) => {
                                                    setMessageText(e.target.value);
                                                    // Clear error when user starts typing
                                                    if (messageError) {
                                                        setMessageError('');
                                                    }
                                                }}
                                                placeholder="Type your message here... Share your thoughts, ask questions, or provide updates."
                                                maxLength={1000}
                                                status={messageError ? 'error' : ''}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && !e.shiftKey && messageText.trim().length >= 10) {
                                                        e.preventDefault();
                                                        handleSendMessage();
                                                    }
                                                }}
                                                style={{
                                                    borderRadius: '10px',
                                                    fontSize: '15px',
                                                    resize: 'vertical',
                                                    borderColor: messageError ? '#ff4d4f' : '#d9d9d9',
                                                    transition: 'all 0.3s ease',
                                                    padding: '12px 16px',
                                                    lineHeight: '1.6'
                                                }}
                                                autoSize={{ minRows: 4, maxRows: 8 }}
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = '#1890ff';
                                                    e.target.style.boxShadow = '0 0 0 2px rgba(24, 144, 255, 0.2)';
                                                }}
                                                onBlur={(e) => {
                                                    if (!messageError) {
                                                        e.target.style.borderColor = '#d9d9d9';
                                                        e.target.style.boxShadow = 'none';
                                                    }
                                                }}
                                            />
                                            {/* Character Progress Bar */}
                                            {messageText.trim().length > 0 && (
                                                <div className="mt-2">
                                                    <div 
                                                        style={{ 
                                                            height: '4px',
                                                            backgroundColor: '#f0f0f0',
                                                            borderRadius: '2px',
                                                            overflow: 'hidden',
                                                            marginBottom: '4px'
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                height: '100%',
                                                                width: `${Math.min((messageText.trim().length / 10) * 100, 100)}%`,
                                                                backgroundColor: messageText.trim().length >= 10 ? '#52c41a' : '#faad14',
                                                                transition: 'all 0.3s ease',
                                                                borderRadius: '2px'
                                                            }}
                                            />
                                        </div>
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <small style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                                            {messageText.trim().length >= 10 ? (
                                                                <span style={{ color: '#52c41a' }}>
                                                                    <i className="bi bi-check-circle me-1"></i>
                                                                    Ready to send
                                                                </span>
                                                            ) : (
                                                                <span style={{ color: '#faad14' }}>
                                                                    {10 - messageText.trim().length} more character{10 - messageText.trim().length !== 1 ? 's' : ''} needed
                                                                </span>
                                                            )}
                                                        </small>
                                                        <small style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                                            {messageText.length} / 1000 characters
                                                        </small>
                                                    </div>
                                                </div>
                                            )}
                                            {messageError && (
                                                <div 
                                                    className="mt-2 d-flex align-items-center gap-2" 
                                                    style={{ 
                                                        color: '#ff4d4f', 
                                                        fontSize: '13px',
                                                        padding: '8px 12px',
                                                        backgroundColor: '#fff2f0',
                                                        borderRadius: '6px',
                                                        border: '1px solid #ffccc7'
                                                    }}
                                                >
                                                    <i className="bi bi-exclamation-circle-fill"></i>
                                                    <span>{messageError}</span>
                                                </div>
                                            )}
                                            {!messageError && messageText.trim().length > 0 && messageText.trim().length < 10 && (
                                                <div 
                                                    className="mt-2 d-flex align-items-center gap-2" 
                                                    style={{ 
                                                        color: '#faad14', 
                                                        fontSize: '13px',
                                                        padding: '8px 12px',
                                                        backgroundColor: '#fffbe6',
                                                        borderRadius: '6px',
                                                        border: '1px solid #ffe58f'
                                                    }}
                                                >
                                                    <i className="bi bi-info-circle-fill"></i>
                                                    <span>Minimum 10 characters required ({10 - messageText.trim().length} more needed)</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="d-flex gap-2 align-items-center flex-wrap">
                                            {canCancel && (
                                                <Button
                                                    danger
                                                    onClick={handleCancelDispute}
                                                    disabled={sending}
                                                    icon={<i className="bi bi-x-circle"></i>}
                                                    size="large"
                                                    style={{
                                                        borderRadius: '8px',
                                                        fontWeight: 500,
                                                        height: '44px',
                                                        padding: '0 20px',
                                                        boxShadow: '0 2px 4px rgba(255, 77, 79, 0.2)'
                                                    }}
                                                >
                                                    Cancel Dispute
                                                </Button>
                                            )}
                                            <Button
                                                onClick={() => {
                                                    setMessageText('');
                                                    setMessageError('');
                                                }}
                                                disabled={!messageText.trim() || sending}
                                                size="large"
                                                style={{
                                                    borderRadius: '8px',
                                                    fontWeight: 500,
                                                    height: '44px',
                                                    padding: '0 20px',
                                                    borderColor: '#d9d9d9'
                                                }}
                                            >
                                                <i className="bi bi-arrow-counterclockwise me-2"></i>
                                                Clear
                                            </Button>
                                            <Button
                                                type="primary"
                                                htmlType="submit"
                                                loading={sending}
                                                disabled={!messageText.trim() || messageText.trim().length < 10}
                                                size="large"
                                                style={{
                                                    minWidth: '160px',
                                                    borderRadius: '8px',
                                                    fontWeight: 600,
                                                    height: '44px',
                                                    padding: '0 24px',
                                                    background: messageText.trim().length >= 10 
                                                        ? 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)' 
                                                        : undefined,
                                                    boxShadow: messageText.trim().length >= 10 
                                                        ? '0 4px 12px rgba(24, 144, 255, 0.4)' 
                                                        : undefined,
                                                    transition: 'all 0.3s ease'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (messageText.trim().length >= 10 && !sending) {
                                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(24, 144, 255, 0.5)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (messageText.trim().length >= 10 && !sending) {
                                                        e.currentTarget.style.transform = 'translateY(0)';
                                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(24, 144, 255, 0.4)';
                                                    }
                                                }}
                                            >
                                                {sending ? (
                                                    <>
                                                        <i className="bi bi-hourglass-split me-2"></i>
                                                        Sending...
                                                    </>
                                                ) : (
                                                    <>
                                                        <SendOutlined className="me-2" />
                                                Send Message
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                        <div 
                                            className="mt-3 pt-3" 
                                            style={{ 
                                                backgroundColor: '#f8f9fa',
                                                padding: '12px 16px',
                                                borderRadius: '8px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                flexWrap: 'wrap'
                                            }}
                                        >
                                            <small className="d-flex align-items-center gap-1" style={{ color: '#595959' }}>
                                                <i className="bi bi-lightbulb text-warning"></i>
                                                <strong>Tips:</strong>
                                            </small>
                                            <small style={{ color: '#8c8c8c' }}>
                                                Press <kbd style={{ 
                                                    padding: '2px 6px', 
                                                    backgroundColor: '#ffffff', 
                                                    color: '#262626',
                                                    borderRadius: '4px',
                                                    border: '1px solid #d9d9d9',
                                                    fontSize: '11px',
                                                    fontWeight: 500,
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                                }}>Enter</kbd> to send
                                            </small>
                                            <small style={{ color: '#bfbfbf' }}>•</small>
                                            <small style={{ color: '#8c8c8c' }}>
                                                <kbd style={{ 
                                                    padding: '2px 6px', 
                                                    backgroundColor: '#ffffff', 
                                                    color: '#262626',
                                                    borderRadius: '4px',
                                                    border: '1px solid #d9d9d9',
                                                    fontSize: '11px',
                                                    fontWeight: 500,
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                                }}>Shift + Enter</kbd> for new line
                                            </small>
                                            <small style={{ color: '#bfbfbf' }}>•</small>
                                            <small style={{ color: '#8c8c8c' }}>
                                                Minimum 10 characters required
                                            </small>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="col-12">
                        <div className="d-flex justify-content-end gap-2 flex-wrap">
                            {isAdmin && (
                                <>
                                    {!disputeData.assigned_admin && (
                                        <Button
                                            type="primary"
                                            size="large"
                                            onClick={() => setAssignModalVisible(true)}
                                            icon={<UserAddOutlined />}
                                        >
                                            Assign to Admin
                                        </Button>
                                    )}
                                    {disputeData.status !== 'resolved' && disputeData.status !== 'closed' && (
                                        <Button
                                            type="primary"
                                            size="large"
                                            onClick={() => setResolveModalVisible(true)}
                                            icon={<CheckCircleOutlined />}
                                            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                                        >
                                            Resolve Dispute
                                        </Button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Assign Modal - Ant Design */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <UserAddOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
                        <span>Assign Dispute</span>
                    </div>
                }
                open={assignModalVisible}
                onCancel={() => {
                    setAssignModalVisible(false);
                    setSelectedAdmin('');
                    assignForm.resetFields();
                }}
                footer={null}
                width={500}
            >
                <Form
                    form={assignForm}
                    layout="vertical"
                    onFinish={handleAssignDispute}
                >
                    <Form.Item
                        label="Select Admin"
                        name="admin"
                        rules={[{ required: true, message: 'Please select an admin' }]}
                    >
                        <Select
                            placeholder="Select admin to assign"
                            size="large"
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {admins.map(admin => {
                                const adminName = `${admin.first_name || ''} ${admin.last_name || ''}`.trim() || admin.email;
                                return (
                                    <Select.Option 
                                        key={admin.id} 
                                        value={admin.id}
                                        label={`${adminName} (${admin.email})`}
                                    >
                                        <div>
                                            <div style={{ fontWeight: 500 }}>
                                                {toTitleCase(adminName)}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                                {admin.email}
                                            </div>
                                        </div>
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </Form.Item>
                    <Form.Item className="mb-0">
                        <div className="d-flex justify-content-end gap-2">
                            <Button
                                onClick={() => {
                                    setAssignModalVisible(false);
                                    setSelectedAdmin('');
                                    assignForm.resetFields();
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                            >
                                Assign
                            </Button>
                        </div>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Resolve Modal - Ant Design */}
            <Modal
                title="Resolve Dispute"
                open={resolveModalVisible}
                onCancel={() => {
                    setResolveModalVisible(false);
                    setResolutionOutcome('');
                    setResolutionDetails('');
                    resolveForm.resetFields();
                }}
                footer={null}
                width={600}
            >
                <Form
                    form={resolveForm}
                    layout="vertical"
                    onFinish={handleResolveDispute}
                >
                    <Form.Item
                        label={
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <span>
                                    Resolution Outcome <span className="text-danger">*</span>
                                </span>
                                <Button
                                    type="link"
                                    icon={<FileTextOutlined />}
                                    onClick={() => setResolutionTemplateModalVisible(true)}
                                    style={{ padding: 0, height: 'auto' }}
                                >
                                    Show Templates
                                </Button>
                            </div>
                        }
                        name="resolutionOutcome"
                        rules={[{ required: true, message: 'Please select a resolution outcome' }]}
                    >
                        <Select
                            placeholder="Select resolution outcome"
                            value={resolutionOutcome}
                            onChange={(value) => setResolutionOutcome(value)}
                            disabled={resolving}
                        >
                            <Select.Option value="favor_filer">In Favor of Filer</Select.Option>
                            <Select.Option value="favor_respondent">In Favor of Respondent</Select.Option>
                            <Select.Option value="partial_refund">Partial Refund</Select.Option>
                            <Select.Option value="full_refund">Full Refund</Select.Option>
                            <Select.Option value="replacement">Replacement</Select.Option>
                            <Select.Option value="no_action">No Action Required</Select.Option>
                            <Select.Option value="cancelled">Cancelled</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        label={
                            <>
                                Resolution Details <span className="text-danger">*</span>
                            </>
                        }
                        name="resolutionDetails"
                        rules={[
                            { required: true, message: 'Please provide resolution details' },
                            { min: 20, message: 'Resolution details must be at least 20 characters' }
                        ]}
                    >
                        <Input.TextArea
                            rows={6}
                            value={resolutionDetails}
                            onChange={(e) => setResolutionDetails(e.target.value)}
                            placeholder="Provide detailed resolution notes..."
                            maxLength={2000}
                            showCount
                        />
                    </Form.Item>
                    <Form.Item className="mb-0">
                        <div className="d-flex justify-content-end gap-2">
                            <Button
                                onClick={() => {
                                    setResolveModalVisible(false);
                                    setResolutionOutcome('');
                                    setResolutionDetails('');
                                }}
                                disabled={resolving}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={resolving}
                                disabled={resolving}
                                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                            >
                                Resolve Dispute
                            </Button>
                        </div>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Template Modal - Ant Design */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <FileTextOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
                        <span>Message Templates</span>
                        {templateSearchQuery.trim() ? (
                            <Tag 
                                color="blue" 
                                style={{ 
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    padding: '6px 14px',
                                    height: 'auto',
                                    lineHeight: '1.5',
                                    borderRadius: '6px',
                                    boxShadow: '0 2px 4px rgba(24, 144, 255, 0.2)'
                                }}
                            >
                                {filteredTemplates.length} of {disputeMessageTemplates.length}
                            </Tag>
                        ) : (
                            <Tag 
                                color="green" 
                                style={{ 
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    padding: '6px 14px',
                                    height: 'auto',
                                    lineHeight: '1.5',
                                    borderRadius: '6px',
                                    boxShadow: '0 2px 4px rgba(82, 196, 26, 0.2)'
                                }}
                            >
                                {disputeMessageTemplates.length} Templates
                            </Tag>
                        )}
                    </div>
                }
                open={templateModalVisible}
                onCancel={() => {
                    setTemplateModalVisible(false);
                    setTemplateSearchQuery('');
                }}
                footer={null}
                width={700}
                style={{ top: 20 }}
            >
                {/* Search Input */}
                <div style={{ marginBottom: '16px' }}>
                    <Input
                        placeholder="Search templates by title or content..."
                        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                        value={templateSearchQuery}
                        onChange={(e) => setTemplateSearchQuery(e.target.value)}
                        allowClear
                        size="large"
                        style={{
                            borderRadius: '8px'
                        }}
                    />
                </div>

                <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '8px 0' }}>
                    {filteredTemplates.length > 0 ? (
                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
                            gap: '12px',
                            padding: '8px'
                        }}>
                            {filteredTemplates.map((template) => (
                            <Card
                                key={template.id}
                                hoverable
                                onClick={() => handleTemplateSelect(template)}
                                style={{
                                    cursor: 'pointer',
                                    borderRadius: '8px',
                                    border: '1px solid #e8e8e8',
                                    transition: 'all 0.3s ease',
                                    height: '100%'
                                }}
                                bodyStyle={{ padding: '16px' }}
                            >
                                <div style={{ 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    gap: '8px'
                                }}>
                                    <div style={{ 
                                        fontSize: '14px', 
                                        fontWeight: 600, 
                                        color: '#262626',
                                        marginBottom: '4px'
                                    }}>
                                        {template.title}
                                    </div>
                                    <div style={{ 
                                        fontSize: '12px', 
                                        color: '#8c8c8c',
                                        lineHeight: '1.5',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: 'vertical'
                                    }}>
                                        {template.message}
                                    </div>
                                    <div style={{ 
                                        marginTop: '8px',
                                        paddingTop: '8px',
                                        borderTop: '1px solid #f0f0f0'
                                    }}>
                                        <Button
                                            type="link"
                                            size="small"
                                            icon={<FileTextOutlined />}
                                            style={{
                                                padding: 0,
                                                height: 'auto',
                                                fontSize: '12px',
                                                color: '#1890ff'
                                            }}
                                        >
                                            Use Template
                                        </Button>
                                    </div>
                                </div>
                            </Card>
                            ))}
                        </div>
                    ) : (
                        <div style={{ 
                            textAlign: 'center', 
                            padding: '60px 20px',
                            color: '#8c8c8c'
                        }}>
                            <SearchOutlined style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }} />
                            <div style={{ fontSize: '16px', fontWeight: 500, marginBottom: '8px' }}>
                                No templates found
                            </div>
                            <div style={{ fontSize: '13px' }}>
                                Try searching with different keywords
                            </div>
                        </div>
                    )}
                </div>
                {filteredTemplates.length > 0 && (
                    <div style={{ 
                        marginTop: '16px', 
                        paddingTop: '16px', 
                        borderTop: '1px solid #f0f0f0',
                        textAlign: 'center'
                    }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            Click on any template to use it in your message
                        </Text>
                    </div>
                )}
            </Modal>

            {/* Resolution Template Modal - Ant Design */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <FileTextOutlined style={{ color: '#52c41a', fontSize: '20px' }} />
                        <span>Resolution Templates</span>
                        {resolutionTemplateSearchQuery.trim() ? (
                            <Tag 
                                color="blue" 
                                style={{ 
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    padding: '6px 14px',
                                    height: 'auto',
                                    lineHeight: '1.5',
                                    borderRadius: '6px',
                                    boxShadow: '0 2px 4px rgba(24, 144, 255, 0.2)'
                                }}
                            >
                                {filteredResolutionTemplates.length} of {resolutionTemplates.length}
                            </Tag>
                        ) : (
                            <Tag 
                                color="green" 
                                style={{ 
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    padding: '6px 14px',
                                    height: 'auto',
                                    lineHeight: '1.5',
                                    borderRadius: '6px',
                                    boxShadow: '0 2px 4px rgba(82, 196, 26, 0.2)'
                                }}
                            >
                                {resolutionTemplates.length} Templates
                            </Tag>
                        )}
                    </div>
                }
                open={resolutionTemplateModalVisible}
                onCancel={() => {
                    setResolutionTemplateModalVisible(false);
                    setResolutionTemplateSearchQuery('');
                }}
                footer={null}
                width={800}
                style={{ top: 20 }}
            >
                {/* Search Input */}
                <div style={{ marginBottom: '16px' }}>
                    <Input
                        placeholder="Search templates by title, outcome, or content..."
                        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                        value={resolutionTemplateSearchQuery}
                        onChange={(e) => setResolutionTemplateSearchQuery(e.target.value)}
                        allowClear
                        size="large"
                        style={{
                            borderRadius: '8px'
                        }}
                    />
                </div>

                <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '8px 0' }}>
                    {filteredResolutionTemplates.length > 0 ? (
                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', 
                            gap: '12px',
                            padding: '8px'
                        }}>
                            {filteredResolutionTemplates.map((template) => (
                            <Card
                                key={template.id}
                                hoverable
                                onClick={() => handleResolutionTemplateSelect(template)}
                                style={{
                                    cursor: 'pointer',
                                    borderRadius: '8px',
                                    border: '1px solid #e8e8e8',
                                    transition: 'all 0.3s ease',
                                    height: '100%'
                                }}
                                bodyStyle={{ padding: '16px' }}
                            >
                                <div style={{ 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    gap: '8px'
                                }}>
                                    <div style={{ 
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        marginBottom: '4px'
                                    }}>
                                        <div style={{ 
                                            fontSize: '14px', 
                                            fontWeight: 600, 
                                            color: '#262626',
                                            flex: 1
                                        }}>
                                            {template.title}
                                        </div>
                                        <Tag 
                                            color={
                                                template.outcome === 'favor_filer' ? 'green' :
                                                template.outcome === 'favor_respondent' ? 'blue' :
                                                template.outcome === 'full_refund' ? 'cyan' :
                                                template.outcome === 'partial_refund' ? 'orange' :
                                                template.outcome === 'replacement' ? 'purple' :
                                                template.outcome === 'no_action' ? 'default' :
                                                'red'
                                            }
                                            style={{ 
                                                fontSize: '10px',
                                                fontWeight: 600,
                                                padding: '2px 8px',
                                                height: 'auto',
                                                lineHeight: '1.4',
                                                borderRadius: '4px',
                                                marginLeft: '8px'
                                            }}
                                        >
                                            {getResolutionOutcomeLabel(template.outcome)}
                                        </Tag>
                                    </div>
                                    <div style={{ 
                                        fontSize: '12px', 
                                        color: '#8c8c8c',
                                        lineHeight: '1.5',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 4,
                                        WebkitBoxOrient: 'vertical'
                                    }}>
                                        {template.details}
                                    </div>
                                    <div style={{ 
                                        marginTop: '8px',
                                        paddingTop: '8px',
                                        borderTop: '1px solid #f0f0f0'
                                    }}>
                                        <Button
                                            type="link"
                                            size="small"
                                            icon={<FileTextOutlined />}
                                            style={{
                                                padding: 0,
                                                height: 'auto',
                                                fontSize: '12px',
                                                color: '#52c41a'
                                            }}
                                        >
                                            Use Template
                                        </Button>
                                    </div>
                                </div>
                            </Card>
                            ))}
                        </div>
                    ) : (
                        <div style={{ 
                            textAlign: 'center', 
                            padding: '60px 20px',
                            color: '#8c8c8c'
                        }}>
                            <SearchOutlined style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }} />
                            <div style={{ fontSize: '16px', fontWeight: 500, marginBottom: '8px' }}>
                                No templates found
                            </div>
                            <div style={{ fontSize: '13px' }}>
                                Try searching with different keywords
                            </div>
                        </div>
                    )}
                </div>
                {filteredResolutionTemplates.length > 0 && (
                    <div style={{ 
                        marginTop: '16px', 
                        paddingTop: '16px', 
                        borderTop: '1px solid #f0f0f0',
                        textAlign: 'center'
                    }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            Click on any template to populate the outcome and details fields
                        </Text>
                    </div>
                )}
            </Modal>

            {/* User Details Modal - Ant Design */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <UserOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
                        <span>User Details</span>
                    </div>
                }
                open={userDetailModalVisible}
                onCancel={() => {
                    setUserDetailModalVisible(false);
                    setSelectedUser(null);
                }}
                footer={null}
                width={600}
            >
                <Spin spinning={userDetailLoading}>
                    {selectedUser ? (
                        <div style={{ padding: '8px 0' }}>
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '16px',
                                marginBottom: '24px',
                                paddingBottom: '24px',
                                borderBottom: '1px solid #f0f0f0'
                            }}>
                                <Avatar 
                                    src={selectedUser.profile_image_url || (selectedUser.profile_image ? `${env.BACKEND_STORAGE_URL}/${selectedUser.profile_image}` : null)}
                                    icon={!selectedUser.profile_image_url && !selectedUser.profile_image ? <UserOutlined /> : null}
                                    size={64}
                                    style={{ 
                                        backgroundColor: '#1890ff',
                                        flexShrink: 0
                                    }}
                                >
                                    {!selectedUser.profile_image_url && !selectedUser.profile_image && selectedUser.first_name ? 
                                        selectedUser.first_name.charAt(0).toUpperCase() : null}
                                </Avatar>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '4px' }}>
                                        {selectedUser.first_name && selectedUser.last_name 
                                            ? `${selectedUser.first_name} ${selectedUser.last_name}`.trim()
                                            : selectedUser.name || 'N/A'}
                                    </div>
                                    <div style={{ fontSize: '14px', color: '#8c8c8c' }}>
                                        {selectedUser.email || 'N/A'}
                                    </div>
                                    {selectedUser.roles && selectedUser.roles.length > 0 && (
                                        <div style={{ marginTop: '8px' }}>
                                            {selectedUser.roles.map((role, index) => (
                                                <Tag 
                                                    key={index}
                                                    color={role === 'admin' ? 'red' : 'blue'}
                                                    style={{ marginTop: '4px' }}
                                                >
                                                    {role.toUpperCase()}
                                                </Tag>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <div>
                                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                                        First Name
                                    </Text>
                                    <Text strong style={{ fontSize: '14px' }}>
                                        {selectedUser.first_name || 'N/A'}
                                    </Text>
                                </div>
                                <div>
                                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                                        Last Name
                                    </Text>
                                    <Text strong style={{ fontSize: '14px' }}>
                                        {selectedUser.last_name || 'N/A'}
                                    </Text>
                                </div>
                                <div>
                                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                                        Email
                                    </Text>
                                    <Text strong style={{ fontSize: '14px' }}>
                                        {selectedUser.email || 'N/A'}
                                    </Text>
                                </div>
                                <div>
                                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                                        Phone
                                    </Text>
                                    <Text strong style={{ fontSize: '14px' }}>
                                        {selectedUser.phone || 'N/A'}
                                    </Text>
                                </div>
                                <div>
                                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                                        Status
                                    </Text>
                                    <Tag color={selectedUser.status === 'active' ? 'green' : selectedUser.status === 'pending' ? 'orange' : 'default'}>
                                        {selectedUser.status ? selectedUser.status.toUpperCase() : 'N/A'}
                                    </Tag>
                                </div>
                                <div>
                                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                                        Email Verified
                                    </Text>
                                    <Tag color={selectedUser.email_verified_at ? 'green' : 'red'}>
                                        {selectedUser.email_verified_at ? 'VERIFIED' : 'NOT VERIFIED'}
                                    </Tag>
                                </div>
                                {selectedUser.created_at && (
                                    <div>
                                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                                            Member Since
                                        </Text>
                                        <Text strong style={{ fontSize: '14px' }}>
                                            {moment(selectedUser.created_at).format('MMMM DD, YYYY')}
                                        </Text>
                                    </div>
                                )}
                                {selectedUser.company && (
                                    <div>
                                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                                            Company
                                        </Text>
                                        <Text strong style={{ fontSize: '14px' }}>
                                            {selectedUser.company.name || selectedUser.company || 'N/A'}
                                        </Text>
                                    </div>
                                )}
                            </div>

                            {selectedUser.address && (
                                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
                                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                                        Address
                                    </Text>
                                    <Text style={{ fontSize: '14px' }}>
                                        {selectedUser.address}
                                    </Text>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#8c8c8c' }}>
                            <UserOutlined style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }} />
                            <div style={{ fontSize: '14px' }}>
                                {userDetailLoading ? 'Loading user details...' : 'No user data available'}
                            </div>
                        </div>
                    )}
                </Spin>
            </Modal>
                    </>
                )}
            </DashboardLayout>
        </Spin>
    );
};

export default DisputeDetail;
